import {
  TNodeWeight,
  TNodeWeightAssignee,
} from 'components/TaskEditorHeader/SmartSchedule';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,
} from 'recharts';
import { IProgressHistory, ITask } from 'store/tasks/tasksType';
import { smartDateString } from 'utils/date';
import { convertDigitToProperFormat } from 'utils/number';
import SanitizedHTML from 'react-sanitized-html';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import BlurModal from 'containers/Profile/Panels/DocumentManager/BlurModal';
import styled from 'styled-components';
import Input from 'components/Input';
import moment from 'moment';
import { blue, gray, green, red } from 'utils/style/colors';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import { SINGLE_DAY_BASE_ON_TIME_STAMP } from 'components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/ResponseItem';

const BlurModalContainer = styled.div`
  max-height: 70vh;
  overflow: auto;
`;

const CustomizedHandlersContainer = styled.div``;

interface IDateProgress {
  maxProgress: number;
  time: number;
  tasks: Array<ITask & TNodeWeight>;
}

interface IProgressHistoryDate {
  time: number;
  progress: number;
}

interface IProgressWithAssigneeWeight {
  time: number;
  assingeeProgress: number;
  taskId: number;
  assingneeId: number;
  userId: number;
  submitWithDalay: boolean;
}

interface IExpectedData {
  expectedProgress: number;
  time: number;
  tasks: (ITask & TNodeWeight)[];
}

const mapProgressHistoryToChartData = (
  history: IProgressHistory['mainProgress']
) => {
  return [...history]
    .sort((a, b) => a.creationDate - b.creationDate)
    .map((item) => ({
      time: item.creationDate,
      progress: item.progress,
    }));
};

const getDate = (date: Date) => {
  return moment(date).format('jYYYY-jMMMM-jD');
};

const mapProgressHistoryToChartEachDay = (
  history: IProgressHistory['mainProgress']
) => {
  return [...history]
    .sort((a, b) => a.creationDate - b.creationDate)
    .reduce((acc: Array<IProgressHistoryDate>, item, index, arr) => {
      if (index + 1 < history.length) {
        const currentProgressDate = new Date(item.creationDate);
        const nextProgressDate = new Date(arr[index + 1].creationDate);
        if (getDate(currentProgressDate) !== getDate(nextProgressDate)) {
          acc.push({ time: item.creationDate, progress: item.progress });
        }
      } else acc.push({ time: item.creationDate, progress: item.progress });

      return acc;
    }, []);
};
const mapTaskToChartData = (
  tasks: Array<ITask & TNodeWeight>,
  showLastUpdate: boolean = false,
  actualData: Array<IProgressHistoryDate> = []
) => {
  const groupedByDate = tasks
    .filter((el) => el.dueDate)
    .reduce<Record<number, IDateProgress>>((acc, el) => {
      const taskDate = new Date(el.dueDate!).setUTCHours(0, 0, 0, 0);
      const actualTaskDate = showLastUpdate
        ? (actualData
            ?.map((el) => el.time)
            ?.find((el) => {
              const aDate = new Date(el);
              aDate.setUTCHours(0, 0, 0, 0);
              return aDate.getTime() === taskDate;
            }) ?? taskDate)
        : taskDate;

      const taskProgress = Number(100 * el.nodeWeight);

      if (acc[actualTaskDate]) {
        acc[actualTaskDate] = {
          ...acc[actualTaskDate],
          maxProgress: acc[actualTaskDate].maxProgress + taskProgress,
          tasks: [...acc[actualTaskDate].tasks, el],
        };
      } else {
        acc[actualTaskDate] = {
          time: actualTaskDate,
          maxProgress: taskProgress,
          tasks: [el],
        };
      }

      return acc;
    }, {});

  const expectedTaskProgress = Object.values(groupedByDate)
    .map((el) => ({
      time: el.time,
      expectedProgress: el.maxProgress,
      tasks: el.tasks,
    }))
    .sort((a, b) => a.time - b.time);

  let acc = 0;
  const accExpectedProgressDate = expectedTaskProgress.map((el) => {
    acc += el.expectedProgress;
    return {
      ...el,
      expectedProgress: Number(acc.toFixed(2)),
    } as IExpectedData;
  });

  return accExpectedProgressDate;
};

const mapTaskAssingeeProgressToChartDate = (
  expectedProgressWithAssigneeWeight: Array<
    ITask & TNodeWeight & TNodeWeightAssignee
  >,
  showLastUpdate: boolean
) => {
  const allProgressWithAssigneeWeight: Array<IProgressWithAssigneeWeight> =
    expectedProgressWithAssigneeWeight
      .filter(
        (el) =>
          el.assignmentHistoryList?.length > 0 &&
          Object.values(el.assignee || {}).length > 0
      )
      .map((el) => {
        return [...el.assignmentHistoryList]
          .sort((a, b) => (a.updateDate > b.updateDate ? 1 : -1))
          .filter((ah) => ah.progress > 0)
          .map((ah, index, array) => {
            if (index === 0) {
              return {
                assingeeProgress: ah.progress * el.assingneeNodeWeight,
                taskId: ah.taskId as number,
                time: ah.updateDate as number,
                assingneeId: ah.id,
                userId: ah.userId,
                submitWithDalay: false,
              };
            }
            const updateDate = new Date(ah.updateDate as number);
            updateDate.setUTCHours(0, 0, 0, 0);
            const lastEditDate = new Date(ah.lastEditDate as number);
            lastEditDate.setUTCHours(0, 0, 0, 0);

            if (ah.progress - array[index - 1].progress === 0) {
              return null;
            }

            return {
              assingeeProgress:
                (ah.progress - array[index - 1].progress) *
                el.assingneeNodeWeight,
              taskId: ah.taskId as number,
              time: ah.updateDate as number,
              assingneeId: ah.id,
              userId: ah.userId,
              submitWithDalay: lastEditDate.getTime() !== updateDate.getTime(),
            };
          });
      })
      .flat()
      .filter((el): el is IProgressWithAssigneeWeight => el !== null);
  const accumulatedProgressWithAssigneeWeight = allProgressWithAssigneeWeight
    .sort((a, b) => (a.time > b.time ? 1 : -1))
    .reduce(
      (
        acc: {
          list: Array<IProgressWithAssigneeWeight>;
          accProgress: number;
        },
        el: IProgressWithAssigneeWeight
      ) => {
        acc.list.push({
          ...el,
          assingeeProgress: acc.accProgress + el.assingeeProgress,
        });
        acc.accProgress += el.assingeeProgress;

        return acc;
      },
      { list: [], accProgress: 0 }
    );

  if (showLastUpdate) {
    const groupByDate = accumulatedProgressWithAssigneeWeight.list.reduce(
      (acc, el) => {
        const date = new Date(el.time);
        date.setUTCHours(0, 0, 0, 0);
        acc[date.getTime()] = el;
        return acc;
      },
      {} as Record<number, IProgressWithAssigneeWeight>
    );
    return Object.values(groupByDate).sort((a, b) =>
      a.time > b.time ? 1 : -1
    );
  } else {
    return accumulatedProgressWithAssigneeWeight.list;
  }
};

const getInitialDateToStartForChart = (time?: number) => {
  const baseTime = time ?? Date.now();
  return baseTime - 24 * 60 * 60 * 1000;
};

const ProgressLineChart = ({
  task,
  expectedProgressWithAssigneeWeight,
  TaskTreeComponent,
}: {
  task: ITask;
  expectedProgressWithAssigneeWeight: Array<
    ITask & TNodeWeight & TNodeWeightAssignee
  >;
  TaskTreeComponent: (dueDate: number) => JSX.Element;
}) => {
  const { t } = useTranslation('taskManager');

  const history = task!.progressHistory!.mainProgress;

  const users = useSelector((state: RootState) =>
    Object.values(state.users.map)
  );

  const [targetDueDate, setTargetDueDate] = useState<number>();
  const [modalStatus, setModalStatus] = useState<boolean>(false);
  const [showLastUpdate, setShowLastUpdate] = useState<boolean>(false);

  const actualData = useMemo(() => {
    if (showLastUpdate) return mapProgressHistoryToChartEachDay(history);
    return mapProgressHistoryToChartData(history!);
  }, [showLastUpdate, history]);

  const properExpectedData = useMemo((): IExpectedData[] => {
    return mapTaskToChartData(
      expectedProgressWithAssigneeWeight,
      showLastUpdate,
      actualData
    );
  }, [expectedProgressWithAssigneeWeight, showLastUpdate]);

  const properActualDataWithAssigneeWeight = useMemo(() => {
    return mapTaskAssingeeProgressToChartDate(
      expectedProgressWithAssigneeWeight,
      showLastUpdate
    );
  }, [expectedProgressWithAssigneeWeight, showLastUpdate]);

  const properActualDataWithAssigneeWeightAndSubmitWithDelay = useMemo(() => {
    return properActualDataWithAssigneeWeight
      .filter((el) => el.submitWithDalay)
      .map(({ assingeeProgress, ...rest }) => ({
        ...rest,
        submitWithDalayProgress: assingeeProgress,
      }));
  }, [properActualDataWithAssigneeWeight, showLastUpdate]);

  const startActualDate = {
    time: getInitialDateToStartForChart(task?.startDate),
    assingeeProgress: 0,
  };

  const startExpectedDate: IExpectedData = {
    expectedProgress: 0,
    time: task?.startDate as number,
    tasks: task
      ? [
          {
            ...task,
            nodeWeight:
              expectedProgressWithAssigneeWeight.find((t) => t.id === task.id)
                ?.nodeWeight ?? 0,
          },
        ]
      : [],
  };

  const todayTime = useMemo(() => {
    const today = new Date();
    return today.getTime();
  }, []);

  const mergedData = useMemo(() => {
    const byTime = new Map<number, any>();
    let rows = [
      startActualDate,
      ...(properExpectedData.length > 0 ? [startExpectedDate] : []),
      ...properExpectedData,
      ...properActualDataWithAssigneeWeight,
      ...properActualDataWithAssigneeWeightAndSubmitWithDelay,
    ];

    const maxTime = rows.reduce((max, row) => {
      const time = Number(row.time);
      return time > max ? time : max;
    }, -Infinity);

    if (
      todayTime &&
      maxTime &&
      (todayTime - maxTime) / SINGLE_DAY_BASE_ON_TIME_STAMP <= 30
    ) {
      rows = [...rows, { time: todayTime }] as any;
    }

    rows.forEach((row) => {
      const time = Number(row.time);
      const prev = byTime.get(time) ?? { time };
      byTime.set(time, { ...prev, ...row });
    });

    return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
  }, [startActualDate, actualData, properExpectedData]);

  const getAssingeeProgressComponent = (item: any, color: string) => {
    const userName =
      users.find((user) => user.id === item.payload.userId)?.name ?? '';
    const taskName =
      expectedProgressWithAssigneeWeight.find(
        (task) => task.id === item.payload.taskId
      )?.title ?? '';

    const taskProgress =
      expectedProgressWithAssigneeWeight.find(
        (task) => task.id === item.payload.taskId
      )?.mainProgress ?? 0;

    return (
      <>
        <div className="flex justify-between">
          <div style={{ color: color, fontWeight: 600 }}>
            {t('assingeeProgress')}
          </div>
          <div>{convertDigitToProperFormat(item.value.toFixed())}%</div>
        </div>
        <hr style={{ margin: '0.5rem 0' }} />
        <div className="flex flex-col gap-2">
          <div
            style={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              fontSize: 10,
            }}
          >
            <div>{t('singleAssignee')}: </div>{' '}
            <div style={{ color: color }}>{userName}</div>
          </div>
          <div
            style={{
              fontWeight: 600,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
              fontSize: 10,
            }}
          >
            <div className="flex flex-col">
              <div>{t('task')} : </div>
              <div style={{ color: color }}>
                <SanitizedHTML
                  html={taskName}
                  allowedAttributes={{ span: ['style'] }}
                />
              </div>
            </div>

            <div className="flex justify-between">
              <div>{t('Progress')} : </div>
              <div>{convertDigitToProperFormat(taskProgress)}%</div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const ProgressTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div
        style={{
          background: 'var(--exact-white)',
          border: '1px solid var(--gray)',
          padding: 12,
          borderRadius: 8,
          minWidth: 220,
        }}
      >
        {payload.map((item: any) => {
          const data = item.payload;

          // ACTUAL PROGRESS
          if (item.dataKey === 'progress') {
            return (
              <div key="progress">
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {smartDateString(Number(label), true, true, true)}
                </div>
                <div className="flex justify-between">
                  <div style={{ color: '#0bbc78', fontWeight: 600 }}>
                    {t('actualProgress')}
                  </div>
                  <div>{convertDigitToProperFormat(item.value.toFixed())}%</div>
                </div>
              </div>
            );
          }

          // EXPECTED PROGRESS
          if (item.dataKey === 'expectedProgress') {
            return (
              <div key="expected">
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {smartDateString(Number(label), false, true, true)}
                </div>
                <div className="flex justify-between">
                  <div style={{ color: blue, fontWeight: 600 }}>
                    {t('expectedProgress')}
                  </div>

                  <div>{convertDigitToProperFormat(item.value.toFixed())}%</div>
                </div>

                {data.tasks?.length > 0 && (
                  <>
                    <hr style={{ margin: '0.5rem 0' }} />
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {t('tasks')}
                    </div>

                    {data.tasks.map((task: any) => (
                      <div
                        key={task.id}
                        style={{
                          fontSize: 12,
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          paddingInlineStart: '0.5rem',
                        }}
                      >
                        <SanitizedHTML
                          allowedAttributes={{ span: ['style'] }}
                          html={task.title}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          }

          if (item.dataKey === 'submitWithDalayProgress') {
            return (
              <div key="submitWithDalay">
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {smartDateString(Number(label), true, true, true)}
                </div>
                {getAssingeeProgressComponent(item, red)}
              </div>
            );
          }

          if (
            item.dataKey === 'assingeeProgress' &&
            !item.payload.submitWithDalay
          ) {
            return (
              <div key="assingee">
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {smartDateString(Number(label), true, true, true)}
                </div>
                {getAssingeeProgressComponent(item, green)}
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  return (
    <div style={{ direction: 'rtl' }}>
      {targetDueDate && modalStatus && (
        <BlurModal
          isOpen={modalStatus}
          onCloseModal={() => setModalStatus((prev) => !prev)}
          title={t('taskDoneTillDueDate', {
            time: smartDateString(new Date(targetDueDate), true, true, true),
          })}
          maxWidth="80%"
        >
          <BlurModalContainer>
            {TaskTreeComponent(targetDueDate)}
          </BlurModalContainer>
        </BlurModal>
      )}
      <CustomizedHandlersContainer>
        <Input
          id="lastUpdateOfEachDay"
          label={t('lastUpdateOfEachDay') as string}
          type="checkbox"
          checked={showLastUpdate}
          onChange={(e) => setShowLastUpdate(e.target.checked)}
        />
      </CustomizedHandlersContainer>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={mergedData}
          margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            allowDuplicatedCategory={false}
            tickFormatter={(value) => smartDateString(value, false, true, true)}
          />

          <YAxis
            dataKey=""
            domain={[0, 100]}
            tickFormatter={(v) => `${convertDigitToProperFormat(v)}%`}
            dx={-30}
          />

          <Tooltip
            labelFormatter={(value) =>
              smartDateString(Number(value), true, true, true)
            }
            formatter={(value) => {
              if (typeof value !== 'number') return value;

              return [
                `${convertDigitToProperFormat(value.toFixed())}%`,
                'Progress',
              ];
            }}
            content={ProgressTooltip}
            offset={-2000}
          />
          {/* <Line
            type="monotone"
            dataKey="progress"
            stroke="#0bbc78"
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{
              r: 6,
              onClick: (e: any) => {
                setModalStatus(true);
                setTargetDueDate(e.payload.time);
              },
            }}
            // label={<ProgressLabel />}
            isAnimationActive
            connectNulls={true}
            name={t('actualProgress') as string}
          /> */}

          <Line
            type="monotone"
            dataKey="expectedProgress"
            stroke={blue}
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{
              r: 6,
            }}
            // label={<ProgressLabel />}
            isAnimationActive
            connectNulls={true}
            name={t('expectedProgress') as string}
          />
          <Line
            type="monotone"
            dataKey="assingeeProgress"
            stroke={'#0bbc78'}
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{
              r: 6,
              onClick: (e: any) => {
                setModalStatus(true);
                setTargetDueDate(e.payload.time);
              },
            }}
            isAnimationActive
            connectNulls={true}
            name={t('assingeeProgress') as string}
          />

          <Line
            type="monotone"
            dataKey="submitWithDalayProgress"
            stroke={red}
            strokeWidth={3}
            dot={{ r: 5 }}
            activeDot={{
              r: 6,
              onClick: (e: any) => {
                setModalStatus(true);
                setTargetDueDate(e.payload.time);
              },
            }}
            isAnimationActive
            name={t('submitWithDalayProgress') as string}
          />

          <ReferenceLine
            x={todayTime}
            stroke={gray}
            strokeWidth={2}
            strokeDasharray="4 4"
            isFront
            ifOverflow="visible"
            label={
              <Label
                value={t('currentDateLine')}
                position="top"
                fill={gray}
                fontSize={11}
                fontWeight="bold"
              />
            }
          />

          <Legend verticalAlign="top" height={25} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProgressLineChart;
